import React, { useEffect, useState, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { StaticRouter } from "react-router-dom/server";
import ScrollToTop from "./components/ScrollToTop";
import { HelmetProvider } from "react-helmet-async";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Briefing from "./pages/Briefing";
import Treinamento from "./pages/Treinamento";
import Admin from "./pages/Admin";
import AdminEmpresa from "./pages/AdminEmpresa";
import Coordenador from "./pages/Coordenador";
import Vendedor from "./pages/Vendedor";
import Upgrade from "./pages/Upgrade";
import AuxiliarVendas from "./pages/AuxiliarVendas";
import AuxiliarVendaSessao from "./pages/AuxiliarVendaSessao";
import SeedData from "./pages/SeedData";
import NotFound from "./pages/NotFound";
import TecnicasFechamento from "./pages/posts/TecnicasFechamento";
import IAVendas from "./pages/posts/IAVendas";
import ObjecoesPreco from "./pages/posts/ObjecoesPreco";
import GamificacaoVendas from "./pages/posts/GamificacaoVendas";
import ColdCalling2026 from "./pages/posts/ColdCalling2026";
import EstrategiasVendas2026 from "./pages/posts/EstrategiasVendas2026";
import ComoVenderMais2026 from "./pages/posts/ComoVenderMais2026";
import PreVendasSDRBDR2026 from "./pages/posts/PreVendasSDRBDR2026";
import SocialSellingLinkedIn2026 from "./pages/posts/SocialSellingLinkedIn2026";
import NegociacaoB2B2026 from "./pages/posts/NegociacaoB2B2026";
import FunilVendas2026 from "./pages/posts/FunilVendas2026";
import TreinamentoVendasIA90Dias from "./pages/posts/TreinamentoVendasIA90Dias";
import TreinamentoVendas from "./pages/posts/TreinamentoVendas";
import CapacitacaoEmVendas from "./pages/posts/CapacitacaoEmVendas";
import LicoesCopa2026Vendas from "./pages/posts/LicoesCopa2026Vendas";
import ComoMontarEquipeVendasDoZero from "./pages/posts/ComoMontarEquipeVendasDoZero";
import ScriptDeVendasPronto from "./pages/posts/ScriptDeVendasPronto";
import ComoTreinarVendedoresNovos from "./pages/posts/ComoTreinarVendedoresNovos";
import MetricasKpisVendas from "./pages/posts/MetricasKpisVendas";
import ProspeccaoB2B from "./pages/posts/ProspeccaoB2B";
import PlaybookDeVendas from "./pages/posts/PlaybookDeVendas";
import GestaoDePipeline from "./pages/posts/GestaoDePipeline";
import RapportPersuasao from "./pages/posts/RapportPersuasao";
import OQueFazConsultorComercial from "./pages/posts/OQueFazConsultorComercial";
import ConsultoriaDeVendas from "./pages/posts/ConsultoriaDeVendas";
import FollowUpQueConverte from "./pages/posts/FollowUpQueConverte";
import PoliticaPrivacidade from "./pages/PoliticaPrivacidade";
import TermosUso from "./pages/TermosUso";
import LGPD from "./pages/LGPD";
import PoliticaCookies from "./pages/PoliticaCookies";
import PoliticaReembolso from "./pages/PoliticaReembolso";
import Consultoria from "./pages/Consultoria";
import Blog from "./pages/Blog";
import CalculadoraROI from "./pages/CalculadoraROI";
import ImersaoLider from "./pages/ImersaoLider";
import VendasPro360 from "./pages/VendasPro360";

interface AppProps {
  url?: string;
}

interface AppRouterProps {
  children: ReactNode;
  url?: string;
}

function AppRouter({ children, url }: AppRouterProps) {
  if (typeof window !== "undefined") {
    return <BrowserRouter>{children}</BrowserRouter>;
  }

  return <StaticRouter location={url ?? "/"}>{children}</StaticRouter>;
}

export default function App({ url }: AppProps) {
  const [queryClient] = useState(() => new QueryClient());

  useEffect(() => {
    // Bloqueia clique direito
    const disableContextMenu = (e: MouseEvent) => e.preventDefault();
    document.addEventListener("contextmenu", disableContextMenu);

    // Bloqueia teclas específicas
    const blockKeys = (e: KeyboardEvent) => {
      if (
        e.key === "F12" ||
        (e.ctrlKey && (e.keyCode === 85 || e.keyCode === 117)) ||
        (e.ctrlKey && e.shiftKey && (e.keyCode === 73 || e.keyCode === 74 || e.keyCode === 67))
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener("keydown", blockKeys);

    // Limpeza ao desmontar
    return () => {
      document.removeEventListener("contextmenu", disableContextMenu);
      document.removeEventListener("keydown", blockKeys);
    };
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <AppRouter url={url}>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/briefing" element={<Briefing />} />
              <Route path="/treinamento" element={<Treinamento />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/empresa/:id" element={<AdminEmpresa />} />
              <Route path="/coordenador" element={<Coordenador />} />
              <Route path="/vendedor" element={<Vendedor />} />
              <Route path="/auxiliar-vendas" element={<AuxiliarVendas />} />
              <Route path="/auxiliar-vendas/sessao/:id" element={<AuxiliarVendaSessao />} />
              <Route path="/upgrade" element={<Upgrade />} />
              <Route path="/seed-data" element={<SeedData />} />
              <Route path="/posts/7-tecnicas-fechamento" element={<TecnicasFechamento />} />
              <Route path="/posts/ia-treinamento-vendas" element={<IAVendas />} />
              <Route path="/posts/objecoes-preco" element={<ObjecoesPreco />} />
              <Route path="/posts/gamificacao-vendas" element={<GamificacaoVendas />} />
              <Route path="/posts/cold-calling-2026" element={<ColdCalling2026 />} />
              <Route path="/posts/13-estrategias-vendas-2026" element={<EstrategiasVendas2026 />} />
              <Route path="/posts/como-vender-mais-2026" element={<ComoVenderMais2026 />} />
              <Route path="/posts/pre-vendas-sdr-bdr-2026" element={<PreVendasSDRBDR2026 />} />
              <Route path="/posts/social-selling-linkedin-2026" element={<SocialSellingLinkedIn2026 />} />
              <Route path="/posts/negociacao-b2b-2026" element={<NegociacaoB2B2026 />} />
              <Route path="/posts/funil-vendas-2026" element={<FunilVendas2026 />} />
              <Route path="/posts/treinamento-de-vendas-com-ia-como-acelerar-resultados-da-equipe-comercial-em-90-dias" element={<TreinamentoVendasIA90Dias />} />
              <Route path="/posts/treinamento-de-vendas" element={<TreinamentoVendas />} />
              <Route path="/posts/capacitacao-em-vendas" element={<CapacitacaoEmVendas />} />
              <Route path="/posts/licoes-copa-2026-vendas" element={<LicoesCopa2026Vendas />} />
              <Route path="/posts/como-montar-equipe-de-vendas-do-zero" element={<ComoMontarEquipeVendasDoZero />} />
              <Route path="/posts/script-de-vendas-pronto" element={<ScriptDeVendasPronto />} />
              <Route path="/posts/como-treinar-vendedores-novos" element={<ComoTreinarVendedoresNovos />} />
              <Route path="/posts/metricas-e-kpis-de-vendas" element={<MetricasKpisVendas />} />
              <Route path="/posts/prospeccao-b2b" element={<ProspeccaoB2B />} />
              <Route path="/posts/playbook-de-vendas" element={<PlaybookDeVendas />} />
              <Route path="/posts/gestao-de-pipeline" element={<GestaoDePipeline />} />
              <Route path="/posts/rapport-persuasao" element={<RapportPersuasao />} />
              <Route path="/posts/o-que-faz-um-consultor-comercial" element={<OQueFazConsultorComercial />} />
              <Route path="/posts/consultoria-de-vendas" element={<ConsultoriaDeVendas />} />
              <Route path="/posts/follow-up-que-converte" element={<FollowUpQueConverte />} />
              <Route path="/politica-privacidade" element={<PoliticaPrivacidade />} />
              <Route path="/termos-uso" element={<TermosUso />} />
              <Route path="/lgpd" element={<LGPD />} />
              <Route path="/politica-cookies" element={<PoliticaCookies />} />
              <Route path="/politica-reembolso" element={<PoliticaReembolso />} />
              <Route path="/consultoria" element={<Consultoria />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/calculadora-roi" element={<CalculadoraROI />} />
              <Route path="/imersao-lider" element={<ImersaoLider />} />
              <Route path="/vendaspro360_docontato_afidelizacao" element={<VendasPro360 />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AppRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}
